import React, { useState, useEffect, useCallback } from 'react'
import { Card, Box, Stack, Group, Avatar, Text, ActionIcon } from '@mantine/core'
import {
  IoHeart,
  IoHeartOutline,
  IoChatbubbleOutline,
  IoShareSocialOutline,
  IoPlay
} from 'react-icons/io5'
import { useHistory } from 'react-router-dom'
import agent from '../../app/api/agent'
import { toAbsoluteMediaUrl } from '../../app/api/resolveApiBaseUrl'
import { CommentsDrawer } from './CommentsDrawer'
import { FEED_CARD_HOVER, FEED_ACTION_BAR_SX } from './feedCardTheme'

export interface PostDto {
  id: string;
  userId: string;
  videoUrl: string;
  thumbnail: string;
  title: string;
  createdAt: string;
  userAvatar: string;
  userName: string;
  likeCount: number;
  commentCount: number;
  linkedCaption?: string;
}

interface PostCardProps {
  post: PostDto;
  onVideoClick?: (postId: string, videoUrl: string) => void;
  onLike?: (postId: string, liked: boolean) => Promise<void>;
  onCommentClick?: (postId: string) => void;
  onShare?: (postId: string) => void;
}

function getRelativeTime (dateString: string): string {
  const diffMs = Date.now() - new Date(dateString).getTime()
  const minutes = Math.floor(diffMs / (1000 * 60))
  const hours = Math.floor(diffMs / (1000 * 60 * 60))
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${days}d ago`
}

export const PostCard: React.FC<PostCardProps> = ({
  post,
  onVideoClick,
  onLike,
  onCommentClick,
  onShare
}) => {
  const history = useHistory()
  const [isLiked, setIsLiked] = useState(false)
  const [isLoadingLike, setIsLoadingLike] = useState(false)
  const [likeCount, setLikeCount] = useState(post.likeCount)
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [commentCount, setCommentCount] = useState(post.commentCount)

  const posterSrc = toAbsoluteMediaUrl(post.thumbnail || post.videoUrl)

  useEffect(() => {
    setLikeCount(post.likeCount)
    setCommentCount(post.commentCount)
  }, [post.id, post.likeCount, post.commentCount])

  const openVideo = () => {
    if (onVideoClick) onVideoClick(post.id, post.videoUrl)
    else history.push(`/slop-feed/${post.id}`)
  }

  const handleLike = useCallback(async () => {
    if (isLoadingLike) return
    setIsLoadingLike(true)
    const prevLiked = isLiked
    const prevCount = likeCount
    try {
      if (onLike) {
        const next = !isLiked
        setIsLiked(next)
        await onLike(post.id, next)
      } else {
        const res = await agent.SlopFeed.toggleLike(post.id)
        setIsLiked(res.isLiked)
        setLikeCount(res.newLikeCount)
      }
    } catch (error) {
      setIsLiked(prevLiked)
      setLikeCount(prevCount)
      console.error('Failed to like post:', error)
    } finally {
      setIsLoadingLike(false)
    }
  }, [isLiked, isLoadingLike, likeCount, onLike, post.id])

  const openComments = () => {
    if (onCommentClick) {
      onCommentClick(post.id)
      return
    }
    setCommentsOpen(true)
  }

  return (
    <>
      <Card
        padding={0}
        radius="md"
        withBorder
        sx={{
          width: '100%',
          overflow: 'hidden',
          borderColor: 'var(--gs-border)',
          backgroundColor: 'var(--gs-bg-2)',
          ...FEED_CARD_HOVER
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          onClick={openVideo}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              openVideo()
            }
          }}
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            backgroundColor: '#000',
            cursor: 'pointer',
            overflow: 'hidden',
            '@media (min-width: 769px)': {
              '&:hover .feed-post-play': {
                transform: 'translate(-50%, -50%) scale(1.08)',
                backgroundColor: 'rgba(0, 0, 0, 0.72)'
              }
            }
          }}
        >
          <img
            src={posterSrc}
            alt={post.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          <Box
            className="feed-post-play"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.55)',
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.2s ease, background 0.2s ease'
            }}
          >
            <IoPlay size={28} color="white" />
          </Box>
        </Box>

        <Box sx={{ padding: '12px 14px', borderTop: '1px solid var(--gs-border)' }}>
          <Group spacing="sm" align="flex-start" noWrap>
            <Avatar src={post.userAvatar} alt={post.userName} size={32} radius="xl" />
            <Stack spacing={4} style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={600} lineClamp={1}>
                {post.userName}
              </Text>
              <Text size="sm" lineClamp={2} c="dimmed">
                {post.title}
              </Text>
              {post.linkedCaption && (
                <Text size="xs" c="orange.6" lineClamp={1}>
                  {post.linkedCaption}
                </Text>
              )}
              <Text size="xs" c="dimmed">
                {getRelativeTime(post.createdAt)}
              </Text>
            </Stack>
          </Group>
        </Box>

        <Group spacing="md" sx={FEED_ACTION_BAR_SX} align="center" wrap="nowrap">
          <ActionIcon
            variant="subtle"
            size="lg"
            color={isLiked ? 'red' : 'gray'}
            onClick={(e) => {
              e.stopPropagation()
              void handleLike()
            }}
            disabled={isLoadingLike}
            aria-label={isLiked ? 'Unlike' : 'Like'}
          >
            {isLiked ? <IoHeart size={22} /> : <IoHeartOutline size={22} />}
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            size="lg"
            color="gray"
            onClick={(e) => {
              e.stopPropagation()
              openComments()
            }}
            aria-label="Comments"
          >
            <IoChatbubbleOutline size={22} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            size="lg"
            color="gray"
            onClick={(e) => {
              e.stopPropagation()
              onShare?.(post.id)
            }}
            aria-label="Share"
            sx={{ marginLeft: 'auto' }}
          >
            <IoShareSocialOutline size={22} />
          </ActionIcon>
          <Group spacing={6} style={{ flexShrink: 0 }}>
            <Text size="xs" c="dimmed">
              {likeCount} {likeCount === 1 ? 'like' : 'likes'}
            </Text>
            <Text size="xs" c="dimmed">
              ·
            </Text>
            <Text size="xs" c="dimmed">
              {commentCount} {commentCount === 1 ? 'comment' : 'comments'}
            </Text>
          </Group>
        </Group>
      </Card>

      <CommentsDrawer
        opened={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        target="slop"
        contentId={post.id}
        onCommentCountChange={setCommentCount}
      />
    </>
  )
}

export default PostCard
